import debounce from 'lodash.debounce';
import {
    EVENT_INIT_SIDEGUIDE_BANNER,
    EVENT_SIDEGUIDE_AD_RENDERED,
    EVENT_DESTROY_SIDEGUIDE_BANNER,
    SIDEGUIDE_BANNER_CONTAINER_ID,
} from 'Utils/types';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

const DISMISS_TYPE = {
    AUTO_DISMISS: 'auto_dismiss',
    MANUAL_DISMISS: 'manual_dismiss',
    COMMENTS_ACTIVE_DISMISS: 'comments_active_dismiss',
    PAGE_CLOSE_DISMISS: 'page_close_dismiss',
    CHATBOT_ACTIVE_DISMISS: 'chatbot_active_dismiss',
}; // these should be in sync with the values in BrandAlertViewed.Dismiss class

const STATE = {
    START: 'start',
    HALF_EXPANDED: 'half-expanded',
    FULL_EXPANDED: 'full-expanded',
    CLOSED: 'closed',
};

const getScrollPosition = (element) => ({
    scrollHeight: element.scrollHeight,
    scrollTop: element.scrollTop,
    clientHeight: element.clientHeight,
});

class HoverTracker {
    constructor(element, callback) {
        this._element = element;
        this._callback = callback;
        this._reset();
        this._track();
    }

    _reset() {
        this._timeEnter = null;
        this._maxY = null;
    }

    _track() {
        this._element.addEventListener('mouseenter', () => {
            this._timeEnter = Date.now();
        });
        this._element.addEventListener('mousemove', (e) => {
            this._maxY = Math.max(
                this._maxY,
                e.clientY + this._element.scrollTop - this._element.getBoundingClientRect().top,
            );
        });
        this._element.addEventListener('mouseleave', () => this.finalize());
    }

    finalize() {
        if (this._timeEnter) {
            const data = { duration: Date.now() - this._timeEnter, maxY: this._maxY };
            this._callback(data);
            this._reset();
        }
    }
}

class BrandAlertPopup {
    constructor(popupElement, data, client, popupNum) {
        this.client = client;
        this.popupNum = popupNum;
        this._dismissTimeSeconds = null;
        this._hoverTrackers = [];
        this._showNextTimeout = null;
        this._shownTime = null;
        this._finalized = false; // guards against tracking a second dismiss for the same alert
        this._sideguideContainerId = null; // set by the client when this alert embeds a banner ad
        this._bannerCreativeId = null; // GAM creative id once the banner fills; sent with tracking

        this._popupState = STATE.START;
        this._popupContainer = document.querySelector('.js_popupContainer');
        this._popup = popupElement;
        this._popupHeader = this._popup.querySelector('.js_popupHeader');
        this._popupHeaderLinks = this._popup.querySelector('.js_headerLinks');
        this._popupHandleBar = this._popup.querySelector('.js_popupHandleBar'); // can be non-present on iOS
        this._popupInnerWrap = this._popup.querySelector('.js_innerWrap');
        this._popupContent = this._popup.querySelector('.js_content');
        this._secondaryWrap = this._popup.querySelector('.js_secondaryWrap');

        this._info = this._popup.querySelector('.js_info');
        this._infoHeader = this._info.querySelector('.js_infoHeader');
        this._infoTitle = this._info.querySelector('.js_isiTitle');
        this._popupInfoContent = this._popup.querySelector('.js_infoContent');

        this._indications = this._popup.querySelector('.js_indications');
        this._indicationsHeader = this._indications.querySelector('.js_indicationsHeader');
        this._indicationsTitle = this._indications.querySelector('.js_indicationsTitle');
        this._indicationsContent = this._indications.querySelector('.js_indicationsContent');

        this._populateData(data);
        this._setUpEvents();
        this.trackEvent({ eventName: 'alert_loaded' });
    }

    _populateData(data) {
        this._popup.classList.toggle('brand-alert-popup--double-isi', !!data.double_isi);

        this._popup.classList.toggle('brand-alert-popup--partial-indication', !!data.partial_indication_enabled);
        if (data.partial_indication_enabled) {
            this._popup.style.setProperty('--partial-indication-height', `${data.partial_indication_height}px`);
        }

        this._popup.classList.toggle('brand-alert-popup--wysiwyg-editor', !!data.wysiwyg_editor);

        // Hide the Indications / ISI trays when the creative opts out (defaults to shown, so only an
        // explicit false hides). Both hidden collapses the secondary wrap — e.g. a banner-only creative.
        this._popup.classList.toggle('brand-alert-popup--no-indications', data.show_indications === false);
        this._popup.classList.toggle('brand-alert-popup--no-isi', data.show_isi === false);

        this._popup.querySelector('.js_headerLinks').innerHTML = data.header;
        // Prepend because there's a dismiss button
        this._popup.querySelector('.js_headerContentWrap').innerHTML = data.headline;
        this._popupContent.innerHTML = data.expanded;
        this._indicationsContent.innerHTML = data.indications;
        this._popupInfoContent.innerHTML = data.isi;
        this._indicationsTitle.innerHTML = data.indications_title;
        this._infoTitle.innerHTML = data.isi_title;
        this._dismissTimeSeconds = data.dismiss_time_seconds;
        this._brandAlertViewedId = data.brand_alert_viewed_id;
        this._brandAlertPlacementId = data.brand_alert_placement_id;
    }

    get brandAlertPlacementId() {
        return this._brandAlertPlacementId;
    }

    _setUpEvents() {
        this._hoverTrackers = [
            // Expanded hover
            new HoverTracker(this._popupInnerWrap, (data) => {
                if (this._shownTime) this.trackEvent({ eventName: 'expanded_hover', data });
            }),
            // ISI hover
            new HoverTracker(this._popupInfoContent, (data) => this.trackEvent({ eventName: 'isi_hover', data })),
            // Indications hover
            new HoverTracker(this._indications, (data) => this.trackEvent({ eventName: 'indications_hover', data })),
        ];

        // block page scrolling when touching the indications header
        this._popupHandleBar?.addEventListener('touchmove', (e) => e.preventDefault());

        // block page scrolling when touching the indications header
        this._indicationsHeader.addEventListener('touchmove', (e) => e.preventDefault());

        // block page scrolling when touching the ISI header
        this._infoHeader.addEventListener('touchmove', (e) => e.preventDefault());

        // handleBar click-to-expand
        this._popupHandleBar?.addEventListener('click', () => this._handleBarClick());

        // indications headline click
        this._indicationsHeader.addEventListener('click', () => this._toggleIndicationsInformation());

        // safety information headline click
        this._infoHeader.addEventListener('click', () => this._toggleSafetyInformation());

        // Manual close
        this._popup
            .querySelector('.js_popupCloseBtn')
            .addEventListener('click', () => this.dismiss(DISMISS_TYPE.MANUAL_DISMISS));

        // Expanded scroll
        this._handleScroll(this._popupInnerWrap, 'expanded_scroll');

        // URL click
        Array.from(this._popup.querySelectorAll('a')).forEach((linkItem) => {
            linkItem.addEventListener('click', () => {
                const data = { text: linkItem.textContent, url: linkItem.href };
                this.trackEvent({ eventName: 'url_click', data });
                return false;
            });
        });

        // Expanded headline click
        this._popupHeader.addEventListener('click', () => {
            if (this._popupState !== STATE.CLOSED) {
                const data = getScrollPosition(this._popupInnerWrap);
                this.trackEvent({ eventName: 'expanded_headline_click', data });
                this._resetShowNextTimeout();
            }
        });

        // Expanded body click
        this._popupContent.addEventListener('click', () => {
            if (this._popupState !== STATE.CLOSED) {
                const data = getScrollPosition(this._popupInnerWrap);
                this.trackEvent({ eventName: 'expanded_click', data });
                this._resetShowNextTimeout();
            }
        });

        // Indications click
        this._indications.addEventListener('click', () => {
            const data = getScrollPosition(this._indications);
            this.trackEvent({ eventName: 'indications_click', data });
            this._resetShowNextTimeout();
        });

        // Indications scroll
        this._handleScroll(this._indicationsContent, 'indications_scroll');

        // ISI scroll
        this._handleScroll(this._popupInfoContent, 'isi_scroll');

        // ISI click
        this._info.addEventListener('click', () => {
            const data = getScrollPosition(this._info);
            this.trackEvent({ eventName: 'isi_click', data });
            this._resetShowNextTimeout();
        });
    }

    trackEvent(eventData) {
        const headers = this.client.getAjaxHeaders();
        const payload = {
            brandAlertViewedId: this._brandAlertViewedId,
            ...eventData,
        };
        // Report which GAM creative rendered (top-level field the tracking serializer expects). Set when
        // the banner fills, so it rides expanded_shown and every later call; stays null (omitted) when the
        // sideguide shows without a fill, and on alert_loaded (fired before the fill).
        if (this._bannerCreativeId != null) payload.bannerCreative = this._bannerCreativeId;
        const data = JSON.stringify(payload);

        $.ajax({
            url: context.brandAlertTrackingUrl,
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
            method: 'POST',
            data,
            headers,
        });
    }

    expand() {
        this.client.expand();

        this._popupContainer.classList.add('is-half-expanded');
        this._popupState = STATE.HALF_EXPANDED;
        this._popupContainer.style.transform = 'translateY(0)';

        this._shownTime = Date.now();

        this.trackEvent({ eventName: 'expanded_shown' });
        this._resetShowNextTimeout();
        this._updateHeaderRows();
    }

    _updateHeaderRows() {
        // Header link rows drive expanded-view styling; 18 = one link row's height in px.
        this._popup.dataset.headerRows = this._popupHeaderLinks.clientHeight / 18;
    }

    getExpandedDurationData() {
        return this._shownTime ? { expandedDuration: Date.now() - this._shownTime } : {};
    }

    finalizeAlert(dismissType, dismissData) {
        // Finalize once. During the post-fill swap the outgoing alert is finalized (auto_dismiss) but
        // stays active for ~600ms; a manual close in that window would otherwise track a second dismiss.
        if (this._finalized) return;
        this._finalized = true;

        let data = this.getExpandedDurationData();
        if (dismissData) {
            data = { ...dismissData, ...data };
        }

        // Cancel the move-to-next countdown so a re-armed timeout can't fire a stray _showNext after
        // this alert is finalized (on dismiss or when it transitions out).
        clearTimeout(this._showNextTimeout);
        this._hoverTrackers.forEach((x) => x.finalize());
        this.trackEvent({ eventName: dismissType, data });
    }

    dismiss(dismissType, data) {
        if (this._popupState === STATE.CLOSED) {
            return;
        }

        this.finalizeAlert(dismissType, data);
        // Mark closed here (before closePopup) so it's platform-independent — the iOS closePopup can't
        // set it, and the dismiss guards in _showNext / its .done handler rely on it on both platforms.
        this._popupState = STATE.CLOSED;
        this.client.closePopup();
        this.client._showComments();
    }

    _toggleSafetyInformation() {
        this._secondaryWrap.classList.toggle('safety-expanded');
        this._secondaryWrap.classList.remove('indications-expanded');
    }

    _toggleIndicationsInformation() {
        this._secondaryWrap.classList.toggle('indications-expanded');
        this._secondaryWrap.classList.remove('safety-expanded');
    }

    resize() {
        this._popupContainer.classList.add('transition-disabled');
        this._popupContainer.style.transform = 'translateY(0)';

        setTimeout(() => {
            this._popupContainer.classList.remove('transition-disabled');
        }, 500);
    }

    _handleBarClick() {
        const windowHeight = window.innerHeight;
        const video = document.querySelector('.js_videoTab video');
        const videoHeight = video.getBoundingClientRect().height;

        if (this._popupState === STATE.FULL_EXPANDED) {
            this._popupContainer.style.height = `calc(100svh - ${videoHeight}px)`;
            this._popupContainer.classList.remove('is-full-expanded');
            this._popupState = STATE.HALF_EXPANDED;
            this.trackEvent({ eventName: 'handlebar_collapsed' });
        } else {
            this._popupContainer.classList.add('is-full-expanded');
            this._popupState = STATE.FULL_EXPANDED;
            this._popupContainer.style.height = `${windowHeight}px`;
            this.trackEvent({ eventName: 'handlebar_expanded' });
        }
    }

    _resetShowNextTimeout() {
        // A finalized alert (transitioned out or dismissed) must never re-arm the move-to-next
        // countdown — otherwise interaction during the swap could fire a stray _showNext that advances
        // the incoming alert early.
        if (this._finalized) return;

        if (this._showNextTimeout) {
            clearTimeout(this._showNextTimeout);
        }

        this._showNextTimeout = setTimeout(() => this.client._showNext(), this._dismissTimeSeconds * 1000);
    }

    _handleScroll(element, eventName) {
        // Source: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
        const checkScrollToBottom = () =>
            Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) <= 1;

        let scrolledToBottom = false;
        element.addEventListener(
            'scroll',
            debounce(() => {
                let trackingEventName = eventName;
                if (!scrolledToBottom && checkScrollToBottom()) {
                    trackingEventName += '_bottom';
                    scrolledToBottom = true;
                }

                this.trackEvent({ eventName: trackingEventName, data: getScrollPosition(element) });
                this._resetShowNextTimeout();
            }, 300),
        );
    }
}

class BrandAlertsClient {
    constructor() {
        this._playStartedTime = null;
        this._expandTimeSeconds = null;
        this._sideguideRequestId = 0;
        this._pendingCaptures = new Map(); // containerId -> fn removing that alert's creative-id listener
        this._showNextPending = false; // in-flight guard for the _showNext fetch (set synchronously)

        this._pristinePopup = null;
        this.activeBrandAlert = null;
        this.nextBrandAlert = null;

        this._popupFrame = document.querySelector('.js_popupFrame');
        // TODO: change this? For channel pages where there's no alerts HTML
        if (!this._popupFrame) {
            return;
        }
    }

    /**
     * Show the brand alert, sideguide (SG).
     *
     * @param {boolean|String} dieAndReport Should be a string representing an event name in case that the brand alert should not be displayed
     *                                      due to some external reason.
     */
    start(dieAndReport = false) {
        this._watchPlaybackStart();
        this._loadAndShowAlert(dieAndReport);
    }

    // Fetch a creative, build its popup, and show it on the normal expand schedule. A sideguide-ad
    // creative also kicks off its ad request here; the show doesn't wait on the ad — it fills into the
    // container during the pre-expand window and the creative id is recorded if/when it renders. Called
    // once per open; if the endpoint has no creative to serve (`.fail`) the popup just stays hidden.
    _loadAndShowAlert(dieAndReport = false) {
        this._loadAd().done((data) => {
            const popup = this._popupFrame.querySelector('.js_brandAlertPopup');
            // Keep a pristine copy of the shell so _showNext can clone a clean popup per rotation.
            this._pristinePopup = popup.cloneNode(true);

            this.activeBrandAlert = new BrandAlertPopup(popup, data, this, 1);

            if (dieAndReport) {
                this.activeBrandAlert.trackEvent({ eventName: dieAndReport });
                return;
            }

            this._expandTimeSeconds = data.expand_time_seconds;

            if (this._expectsSideguideAd(this.activeBrandAlert._popup)) {
                // This creative embeds a sideguide ad slot. Request the ad now and let it fill in the
                // background — the show isn't gated on it: the sideguide appears on the normal expand
                // schedule and records the creative id if/when the ad renders (which may land after
                // expanded_shown on a fast open). Each request gets its own container id.
                const containerId = this._assignSideguideContainerId(this.activeBrandAlert);
                this._captureBannerCreative(containerId, this.activeBrandAlert);
                this._initSideguideBanner(containerId, this.activeBrandAlert);
            }
            // Show on the normal schedule whether or not this creative has an ad.
            this._showSideguide();
        });
    }

    _expectsSideguideAd(root) {
        // A sideguide-ad creative embeds the ad container div (baked into its expanded_html by the admin
        // editor). Detect it by that known constant id — no dependency on __VUMEDI_BANNER_CONTEXT__, which
        // is absent on the iOS brand-alert page. Scope the lookup to a specific popup: during a _showNext
        // transition two popups sit in the DOM at once, so a document-wide lookup could resolve the other.
        // Intentionally does NOT check backend banner config: on a page where the banners module isn't
        // loaded (initialize_banners bailed), nothing handles EVENT_INIT_SIDEGUIDE_BANNER, so the ad div
        // just stays empty (collapsed) — the always-show path means no hang, unlike the old gated version.
        return Boolean(root.querySelector(`#${SIDEGUIDE_BANNER_CONTAINER_ID}`));
    }

    _assignSideguideContainerId(brandAlert) {
        // Give this alert's ad container a unique id so an outgoing and an incoming banner can be on
        // screen at once without their GAM slots colliding. The creative bakes in the base id; we
        // detect on that (see _expectsSideguideAd) then rename to base-N here.
        const containerId = `${SIDEGUIDE_BANNER_CONTAINER_ID}-${(this._sideguideRequestId += 1)}`;
        brandAlert._popup.querySelector(`#${SIDEGUIDE_BANNER_CONTAINER_ID}`).id = containerId;
        brandAlert._sideguideContainerId = containerId;
        return containerId;
    }

    _destroySideguideBanner(brandAlert) {
        // A banner slot lives exactly as long as its popup: tell the banners module to destroy it when
        // the popup is discarded (skip, no-fill, transition-out, dismiss).
        if (!brandAlert?._sideguideContainerId) return;
        this._stopCapture(brandAlert._sideguideContainerId);
        document.dispatchEvent(
            new CustomEvent(EVENT_DESTROY_SIDEGUIDE_BANNER, {
                detail: { containerId: brandAlert._sideguideContainerId },
            }),
        );
        brandAlert._sideguideContainerId = null;
    }

    _initSideguideBanner(containerId, brandAlert = this.activeBrandAlert) {
        // Ask the banners module to build the sideguide ad slot for the given creative; the placement id
        // is applied as the ba_placement_id GAM target (a dedicated key, not the video placement_id one).
        document.dispatchEvent(
            new CustomEvent(EVENT_INIT_SIDEGUIDE_BANNER, {
                detail: {
                    placementId: brandAlert.brandAlertPlacementId,
                    containerId,
                },
            }),
        );
    }

    _captureBannerCreative(containerId, brandAlert) {
        // The ad requests at page load and usually fills before the popup expands, so the show isn't gated
        // on it. Passively record the GAM creative id when the render for this container arrives, so
        // expanded_shown (and later events) can report it via bannerCreative. Keyed per container (not a
        // single global slot) so arming the incoming alert's capture doesn't drop the outgoing alert's.
        // Only a NON-empty render resolves it: an empty render may be followed by a fill (a display()+
        // refresh() sequence, or a synchronous no-fill signal), so keep listening; teardown/destroy cleans
        // up the rest.
        this._stopCapture(containerId);
        const onRendered = (e) => {
            if (e.detail?.containerId !== containerId || e.detail?.isEmpty) return;
            brandAlert._bannerCreativeId = e.detail.creativeId;
            this._stopCapture(containerId);
        };
        document.addEventListener(EVENT_SIDEGUIDE_AD_RENDERED, onRendered);
        this._pendingCaptures.set(containerId, () =>
            document.removeEventListener(EVENT_SIDEGUIDE_AD_RENDERED, onRendered),
        );
    }

    _stopCapture(containerId) {
        const stop = this._pendingCaptures.get(containerId);
        if (stop) {
            stop();
            this._pendingCaptures.delete(containerId);
        }
    }

    _showSideguide() {
        this._setPopupHeight();
        this._popupFrame.classList.remove('is-hidden');
        this._checkPlayStarted();
        this._handleEvents();
    }

    _loadAd() {
        const data = {
            viewed_uuid: context.viewedUUID,
            ...this._getLoadAjaxData(),
        };
        const headers = this.getAjaxHeaders();

        return $.ajax({
            url: context.brandAlertCreativeUrl,
            headers,
            data,
        });
    }

    _checkPlayStarted() {
        if (!this._playStartedTime) {
            setTimeout(() => this._checkPlayStarted(), 1000);
        } else {
            const remainingTimeout = Math.max(this._expandTimeSeconds * 1000 - (Date.now() - this._playStartedTime), 0);
            setTimeout(() => this._showExpanded(), remainingTimeout);
        }
    }

    _showExpanded() {
        if (this._commentsVisible()) {
            this.activeBrandAlert.trackEvent({ eventName: 'comments_prevent_showing' });
            return;
        }

        if (this._isFullscreen() || this._tooSmallScreenHeight() || this._interstitialVisible()) {
            // TODO Track this event
            setTimeout(() => this._showExpanded(), 1000);
        } else {
            this._hideComments();
            this.activeBrandAlert.expand();
        }
    }

    _handleEvents() {
        this._commentEvents();
        this._fullscreenEvents();
        this._unloadEvents();
        this._resizeEvents();
        this._focusInEvents();
        this._focusOutEvents();
    }

    _showNext() {
        // Ignore re-entry while a transition is already in flight. nextBrandAlert covers the fill-wait
        // window, but it's only set inside the async .done below — so _showNextPending, set synchronously
        // here, also blocks a second call landing during the fetch round-trip.
        if (this.activeBrandAlert._popupState === STATE.CLOSED || this.nextBrandAlert || this._showNextPending) {
            return;
        }
        this._showNextPending = true;

        const headers = this.getAjaxHeaders();

        $.ajax({
            url: context.brandAlertCreativeUrl,
            headers,
            data: { viewed_uuid: context.viewedUUID },
        })
            .done((data) => {
                // Fetch done; nextBrandAlert (set just below) now guards re-entry through the fill wait.
                this._showNextPending = false;
                // If the alert was dismissed while this fetch was in flight, don't resurrect it.
                if (this.activeBrandAlert._popupState === STATE.CLOSED) return;
                const popup = this._popupFrame.querySelector('.js_brandAlertPopup');
                // Clone the pristine shell (not the live/rendered popup) so the incoming alert starts
                // clean — no stale ad iframe, no duplicate rendered banner. BrandAlertPopup repopulates
                // it from `data`, injecting the ad container only if this creative is a banner.
                const nextPopup = this._pristinePopup.cloneNode(true);
                nextPopup.classList.add('slide-right');
                popup.after(nextPopup);
                const { popupNum } = this.activeBrandAlert;
                this.nextBrandAlert = new BrandAlertPopup(nextPopup, data, this, popupNum + 1);

                if (this._expectsSideguideAd(this.nextBrandAlert._popup)) {
                    // Incoming alert embeds a banner: request the ad and record its creative id when it
                    // renders, but transition on the normal schedule — the ad fills into the container
                    // after the swap (it may be briefly empty until then).
                    const containerId = this._assignSideguideContainerId(this.nextBrandAlert);
                    this._captureBannerCreative(containerId, this.nextBrandAlert);
                    this._initSideguideBanner(containerId, this.nextBrandAlert);
                }
                // Transition straight away whether or not the incoming creative has an ad.
                this.activeBrandAlert.finalizeAlert(DISMISS_TYPE.AUTO_DISMISS);
                setTimeout(() => {
                    this._makeTransition();
                }, 100);
            })
            .fail(() => {
                this._showNextPending = false;
                this.activeBrandAlert.dismiss(DISMISS_TYPE.AUTO_DISMISS);
            });
    }

    _teardownSideguideBanners() {
        // On close/dismiss, tear down every banner slot this client owns so none leaks a defined GAM slot:
        // drop any half-built incoming alert (which would also wedge the _showNext re-entry guard),
        // destroy the shown alert's slot. Invariant: every pending creative-id listener is keyed by a live
        // alert's container id (both _captureBannerCreative calls pair with _assignSideguideContainerId), so
        // destroying nextBrandAlert + activeBrandAlert routes each through _destroySideguideBanner →
        // _stopCapture — no listener escapes, so no separate pre-clear is needed here.
        if (this.nextBrandAlert) {
            this._destroySideguideBanner(this.nextBrandAlert);
            this.nextBrandAlert._popup.remove();
            this.nextBrandAlert = null;
        }
        // Clear the in-flight guard too, so a close racing a fetch that never resolves can't wedge it.
        this._showNextPending = false;
        this._destroySideguideBanner(this.activeBrandAlert);
    }

    _makeTransition() {
        // The incoming alert can be torn down before this fires — a dismiss during the ~100ms settle
        // after a fill runs _teardownSideguideBanners, which nulls nextBrandAlert. Nothing to transition to.
        if (!this.nextBrandAlert) return;

        this.activeBrandAlert._popup.classList.add('slide-left');
        this.nextBrandAlert._popup.classList.remove('slide-right');

        setTimeout(() => {
            // A dismiss during the 500ms slide runs _teardownSideguideBanners, nulling nextBrandAlert —
            // abort rather than promote null. Teardown already destroyed the outgoing slot and closePopup
            // hid it.
            if (!this.nextBrandAlert) return;

            // The outgoing popup leaves the DOM now — tear down its banner slot if it had one.
            this._destroySideguideBanner(this.activeBrandAlert);
            this.activeBrandAlert._popup.remove();
            this.activeBrandAlert = this.nextBrandAlert;
            this.nextBrandAlert = null;
            this.activeBrandAlert._popupState = STATE.HALF_EXPANDED;
            this.activeBrandAlert.trackEvent({ eventName: 'expanded_shown' });
            this.activeBrandAlert._resetShowNextTimeout();
            this.activeBrandAlert._shownTime = Date.now();
            // Re-apply per-alert layout the pristine clone doesn't carry: header-row count and the
            // platform popup styles (iOS writes inline top/height in _setPopupHeight).
            this.activeBrandAlert._updateHeaderRows();
            this._setPopupHeight();
        }, 500);
    }
}

export class WebBrandAlertsClient extends BrandAlertsClient {
    constructor(player) {
        super();
        this._player = player;
        this._vmComments = document.querySelector('vm-video-comments');
    }

    get _commentsList() {
        return this._vmComments?.shadowRoot?.querySelector('vm-video-comments-list');
    }

    get _commentsWrapper() {
        return this._vmComments?.shadowRoot?.querySelector('.js_commentsWrapper');
    }

    get _commentBox() {
        return this._vmComments?.querySelector('.js_commentBox');
    }

    _watchPlaybackStart() {
        const setPlayStartedTime = () => {
            if (this._playStartedTime) {
                return;
            }

            if (this._player.ended()) {
                return;
            }

            // Backdate the start time based on the current playhead, if available.
            this._playStartedTime = Date.now() - this._player.currentTime() * 1000;
        };

        if (!this._player.paused() && !this._player.ended()) {
            setPlayStartedTime();
        }

        this._player.one('play', setPlayStartedTime);
        this._player.one('contentresumed', setPlayStartedTime);
    }

    _setPopupHeight() {
        if (window.innerWidth >= 992) {
            const relatedContentOffsetTop = document.querySelector('.related-sidebar').offsetTop;
            const result = window.innerHeight - relatedContentOffsetTop;
            this._popupFrame.style.height = `${result}px`;
        }
    }

    _getLoadAjaxData() {
        const searchParams = new URLSearchParams(window.location.search);
        const forceBrandAlertCreativeId = searchParams.get('force_brand_alert_creative_id');

        if (forceBrandAlertCreativeId) {
            return { force_brand_alert_creative_id: forceBrandAlertCreativeId };
        }

        return {};
    }

    getAjaxHeaders() {
        return { 'X-CSRFToken': window.csrftoken() || context.csrftoken, Accept: 'application/json' };
    }

    expand() {
        const video = document.querySelector('.js_videoTab video');
        const videoHeight = video.getBoundingClientRect().height;
        const videoTopOffset = video.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const portraitOrientation = windowWidth < windowHeight;

        // on mobile
        // if the video is bellow the top of the viewport (not scrolled over)
        // scroll the page so that the top of the video aligns with the top of the viewport
        // and align the top of the alert with the bottom of the video
        // Double scroll because of potential Apple smart banner on iOS Safari w/ app installed
        if (portraitOrientation && windowWidth < 992) {
            if (videoTopOffset > 0) {
                setTimeout(() => video.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' }), 100);
                video.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
            }
            this.activeBrandAlert._popupContainer.style.height = `calc(100svh - ${videoHeight}px)`;
        }
    }

    _commentsVisible() {
        // If the comments list is not present or empty, say it's not visible
        if (!this._commentsList || !this._commentsList?.hasComments) {
            return false;
        }

        // If present, check if it's in viewport
        const { top, left, bottom, right } = this._commentsList.getBoundingClientRect();
        const { innerHeight, innerWidth } = window;

        const commentsInViewport =
            ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) &&
            ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth));

        // Visible if writing comments, or comments in viewport
        return document.activeElement === this._commentBox || commentsInViewport;
    }

    _isFullscreen() {
        return this._player.isFullscreen();
    }

    _tooSmallScreenHeight() {
        return window.innerHeight < 390;
    }

    _interstitialVisible() {
        return !!document.querySelector('#subscriptions-form-modal.show');
    }

    _hideComments() {
        if (this._vmComments) {
            this._commentsWrapper?.classList.add('is-collapsed');
            this._vmComments.showAllButtonVisible = true;
        }
    }

    _showComments() {
        if (!this._vmComments) {
            return;
        }

        this._commentsWrapper.classList.remove('is-collapsed');
        this._vmComments.showAllButtonVisible = false;

        if (
            this.activeBrandAlert &&
            (this.activeBrandAlert._popupState !== STATE.START || this.activeBrandAlert._popupState !== STATE.CLOSED)
        ) {
            this.activeBrandAlert.dismiss(DISMISS_TYPE.COMMENTS_ACTIVE_DISMISS);
        }
    }

    _commentEvents() {
        // Dismiss brand alert if "Show All Comments" button is clicked
        this._vmComments?.addEventListener('show-all-comments', () => this._showComments());

        // Dismiss brand alert if comments box is focused
        this._vmComments?.addEventListener('focusin', (e) => {
            if (e.target.closest('.js_commentBox')) {
                this._showComments();
            }
        });
    }

    _fullscreenEvents() {
        // Dismiss if enter full screen
        this._player.on('fullscreenchange', () => {
            if (this._player.isFullscreen() && this.activeBrandAlert._shownTime) {
                this.activeBrandAlert.dismiss(DISMISS_TYPE.AUTO_DISMISS, { reason: 'full screen' });
            }
        });
    }

    _unloadEvents() {
        // Page close
        window.addEventListener('beforeunload', () => {
            this.activeBrandAlert.dismiss(DISMISS_TYPE.PAGE_CLOSE_DISMISS);
        });
    }

    _resizeEvents() {
        // Responsive resizing
        window.addEventListener('resize', () => {
            // recalculate the number of header link rows to apply correct styles for expanded view
            if (this.activeBrandAlert._popup) {
                this.activeBrandAlert._updateHeaderRows();
            }

            if (this.activeBrandAlert && this.activeBrandAlert._popupState === STATE.START) return;

            if (window.innerHeight < 390) {
                this.activeBrandAlert.dismiss(DISMISS_TYPE.AUTO_DISMISS, { reason: 'height < 390px' });
            } else {
                this._setPopupHeight();
            }
        });
    }

    _focusOutEvents() {
        window.addEventListener('blur', () => {
            const data = this.activeBrandAlert.getExpandedDurationData();
            this.activeBrandAlert.trackEvent({ eventName: 'window_focus_lost', data });
        });
    }

    _focusInEvents() {
        window.addEventListener('focus', () => {
            this.activeBrandAlert.trackEvent({ eventName: 'window_focus_retrieved' });
        });
    }

    closePopup() {
        this._teardownSideguideBanners();
        this.activeBrandAlert._popupContainer.classList.add('is-closed');
        this.activeBrandAlert._popup.removeAttribute('style');
    }
}

export class IosBrandAlertsClient extends BrandAlertsClient {
    constructor() {
        super();
        this._commentsVisibleFlag = false;
        this._fullscreenActive = false;
        this._screenTooSmall = false;
        this._interstitialVisibleFlag = false;
    }

    _watchPlaybackStart() {
        // iOS:
        //      On playback start: window.dispatchEvent(new Event('ios_playback_started'));
        window.addEventListener('ios_playback_started', () => (this._playStartedTime = Date.now()));
    }

    _setPopupHeight() {
        const popup = this.activeBrandAlert._popup;
        popup.style.top = '24px';
        this._popupFrame.style.height = '100vh';

        // Scope to the active popup — during a transition two popups are in the DOM, so a document-wide
        // query could resolve the outgoing one.
        if (popup.classList.contains('brand-alert-popup--double-isi')) {
            this.activeBrandAlert._secondaryWrap.style.top = '20px';
        }
    }

    _getLoadAjaxData() {
        // TODO: Still need to get this into context on iOS somehow
        if (context.forceBrandAlertCreativeId) {
            return { force_brand_alert_creative_id: context.forceBrandAlertCreativeId };
        }

        return {};
    }

    getAjaxHeaders() {
        return { Authorization: context.accessToken, Accept: 'application/json' };
    }

    expand() {
        // iOS: expand the popup to fist snap point
        this.activeBrandAlert._popupContainer.style.height = '100vh';
        window.location = 'expand';

        setTimeout(() => {
            // disable transitions for smoother expanding on ios
            this.activeBrandAlert._popupContainer.classList.add('transitions-disabled');
        }, 500);
    }

    _commentsVisible() {
        return this._commentsVisibleFlag;
    }

    _isFullscreen() {
        return this._fullscreenActive;
    }

    _tooSmallScreenHeight() {
        return this._screenTooSmall;
    }

    _interstitialVisible() {
        return this._interstitialVisibleFlag;
    }

    _hideComments() {
        // Cannot happen on iPhone
    }

    _showComments() {
        // Cannot happen on iPhone
    }

    _handleEvents() {
        super._handleEvents();
        this._interstitialEvents();
        this._handleBarClick();
    }

    _interstitialEvents() {
        // iOS:
        //      On interstitial window visible: window.dispatchEvent(new Event('ios_interstitial_visible'));
        //      On interstitial window invisible: window.dispatchEvent(new Event('ios_interstitial_invisible'));
        window.addEventListener('ios_interstitial_visible', () => (this._interstitialVisibleFlag = true));
        window.addEventListener('ios_interstitial_invisible', () => (this._interstitialVisibleFlag = false));
    }

    _commentEvents() {
        // iOS:
        //      On comment window active: window.dispatchEvent(new Event('ios_comments_visible'));
        //      On comment window inactive: window.dispatchEvent(new Event('ios_comments_invisible'));
        window.addEventListener('ios_comments_visible', () => (this._commentsVisibleFlag = true));
        window.addEventListener('ios_comments_invisible', () => (this._commentsVisibleFlag = false));
    }

    _fullscreenEvents() {
        // iOS:
        //      On fullscreen enter: window.dispatchEvent(new Event('ios_fullscreen_enter'));
        //      On fullscreen exit: window.dispatchEvent(new Event('ios_fullscreen_exit'));
        window.addEventListener('ios_fullscreen_enter', () => {
            this._fullscreenActive = true;
            if (this.activeBrandAlert._shownTime) {
                this.activeBrandAlert.dismiss('auto_dismiss', { reason: 'full screen' });
            }
        });

        window.addEventListener('ios_fullscreen_exit', () => (this._fullscreenActive = false));
    }

    _unloadEvents() {
        // iOS:
        //      On leaving page: window.dispatchEvent(new Event('ios_unload'));
        window.addEventListener('ios_unload', () => this.activeBrandAlert.dismiss(DISMISS_TYPE.PAGE_CLOSE_DISMISS));
    }

    _resizeEvents() {
        // iOS:
        //      On video screen load if the screen is <390px high:
        //      window.dispatchEvent(new Event('ios_screen_too_small'));
        window.addEventListener('ios_screen_too_small', () => (this._screenTooSmall = true));
    }

    _focusOutEvents() {
        window.addEventListener('ios_focus_lost', () => {
            const data = this.activeBrandAlert.getExpandedDurationData();
            this.activeBrandAlert.trackEvent({ eventName: 'window_focus_lost', data });
        });
    }

    _focusInEvents() {
        window.addEventListener('ios_focus_retrieved', () => {
            this.activeBrandAlert.trackEvent({ eventName: 'window_focus_retrieved' });
        });
    }

    _handleBarClick() {
        window.addEventListener('ios_handlebar_expanded', () => {
            this.activeBrandAlert.trackEvent({ eventName: 'handlebar_expanded' });
        });

        window.addEventListener('ios_handlebar_collapsed', () => {
            this.activeBrandAlert.trackEvent({ eventName: 'handlebar_collapsed' });
        });
    }

    closePopup() {
        this._teardownSideguideBanners();
        // iOS: should collapse the popup window
        window.location = 'close';
    }
}
